// client/src/pages/admin/CustomersPage.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Form, Card, Spinner, Pagination } from 'react-bootstrap';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import EditCustomerModal from '../../components/modals/EditCustomerModal';
import { FaSearch, FaUserPlus } from 'react-icons/fa';

const CustomersPage = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [showCreateModal, setShowCreateModal] = useState(false);

    const fetchCustomers = useCallback(async (page = 1) => {
        if (!user) return;
        setIsLoading(true);
        try {
            const params = { page, limit: 20 };
            if (searchTerm) {
                params.term = searchTerm;
            }
            const response = await api.get('/customers', { params });
            setCustomers(response.data.data.customers);
            setPagination({
                currentPage: response.data.data.currentPage,
                totalPages: response.data.data.totalPages,
            });
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setIsLoading(false);
        }
    }, [user, searchTerm]);

    useEffect(() => {
        fetchCustomers(1);
    }, [fetchCustomers]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchCustomers(1);
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            fetchCustomers(pageNumber);
        }
    };

    const handleRowClick = (customerId) => {
        navigate(`/admin/customers/${customerId}`);
    };

    const renderPagination = () => {
        const { currentPage, totalPages } = pagination;
        if (totalPages <= 1) return null;

        const pageItems = [];

        pageItems.push(<Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={currentPage === 1} />);
        pageItems.push(<Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />);

        const pageNeighbours = 1;
        let startPage = Math.max(1, currentPage - pageNeighbours);
        let endPage = Math.min(totalPages, currentPage + pageNeighbours);

        if (currentPage - pageNeighbours <= 2) {
            endPage = Math.min(1 + (pageNeighbours * 2), totalPages);
        }
        if (currentPage + pageNeighbours >= totalPages - 1) {
            startPage = Math.max(1, totalPages - (pageNeighbours * 2));
        }

        if (startPage > 1) {
            pageItems.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>{1}</Pagination.Item>);
            if (startPage > 2) {
                pageItems.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            pageItems.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageItems.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            pageItems.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }

        pageItems.push(<Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />);
        pageItems.push(<Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />);

        return <Pagination className="justify-content-center mb-0 mt-3">{pageItems}</Pagination>;
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>고객 관리</h3>
                    <p>등록된 고객 정보를 관리합니다</p>
                </div>
                <div className="page-header-actions">
                    {hasPermission(user?.role, 'customer:create') && (
                        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                            <FaUserPlus className="me-1" style={{ fontSize: 12 }} /> 신규 고객
                        </Button>
                    )}
                </div>
            </div>

            <Card className="search-card">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <div className="d-flex gap-2">
                            <div className="position-relative flex-grow-1">
                                <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)', fontSize: 13 }} />
                                <Form.Control
                                    type="text"
                                    placeholder="고객 이름 또는 연락처로 검색..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    style={{ paddingLeft: 36 }}
                                />
                            </div>
                            <Button type="submit" variant="primary" disabled={isLoading}>
                                {isLoading ? <Spinner as="span" size="sm" /> : '검색'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body className="p-0">
                    {/* PC 테이블 */}
                    <Table hover responsive className="mb-0 d-none d-md-table">
                        <thead>
                            <tr>
                                <th>고객명</th>
                                <th>연락처</th>
                                <th>포인트</th>
                                <th>특이사항</th>
                                <th>등록일</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="5" className="text-center py-5"><Spinner size="sm" /> <span className="text-muted ms-2">불러오는 중...</span></td></tr>
                            ) : customers.length === 0 ? (
                                <tr><td colSpan="5">
                                    <div className="empty-state">
                                        <div className="empty-state-icon">👥</div>
                                        <div className="empty-state-title">등록된 고객이 없습니다</div>
                                        <div className="empty-state-desc">신규 고객을 등록해보세요</div>
                                    </div>
                                </td></tr>
                            ) : customers.map(customer => (
                                <tr key={customer.id} onClick={() => handleRowClick(customer.id)} style={{ cursor: 'pointer' }}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <div style={{
                                                width: 32, height: 32, borderRadius: '50%',
                                                backgroundColor: 'var(--color-primary-light)',
                                                color: 'var(--color-primary)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0
                                            }}>
                                                {customer.customerName?.charAt(0) || '?'}
                                            </div>
                                            <span className="fw-medium">{customer.customerName}</span>
                                        </div>
                                    </td>
                                    <td className="text-muted">{customer.phoneNumber?.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')}</td>
                                    <td><span className="fw-medium">{(customer.points || 0).toLocaleString()}</span> <small className="text-muted">P</small></td>
                                    <td className="text-muted" style={{ maxWidth: 200, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={customer.comment || ''}>{customer.comment || '-'}</td>
                                    <td className="text-muted">{new Date(customer.created_at).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* 모바일 카드 */}
                    <div className="d-md-none mob-list">
                        {isLoading ? (
                            <div className="text-center py-4"><Spinner size="sm" /> <span className="text-muted ms-2">불러오는 중...</span></div>
                        ) : customers.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-state-icon">👥</div>
                                <div className="empty-state-title">등록된 고객이 없습니다</div>
                                <div className="empty-state-desc">신규 고객을 등록해보세요</div>
                            </div>
                        ) : customers.map(customer => (
                            <div key={customer.id} className="mob-card mob-card-clickable" onClick={() => handleRowClick(customer.id)}>
                                <div className="mob-cst-main">
                                    <div className="mob-cst-avatar">
                                        {customer.customerName?.charAt(0) || '?'}
                                    </div>
                                    <div className="mob-cst-body">
                                        <div className="mob-cst-name">{customer.customerName}</div>
                                        <div className="mob-cst-phone">
                                            {customer.phoneNumber?.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3') || '연락처 미등록'}
                                        </div>
                                        {customer.comment && (
                                            <div className="mob-cst-comment">{customer.comment}</div>
                                        )}
                                    </div>
                                    <div className="mob-cst-pts">
                                        <div className="mob-cst-pts-num">{(customer.points || 0).toLocaleString()}</div>
                                        <div className="mob-cst-pts-label">P</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!isLoading && customers.length > 0 && (
                        <div className="px-3 pb-3">{renderPagination()}</div>
                    )}
                </Card.Body>
            </Card>

            <EditCustomerModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSuccess={() => {
                    setShowCreateModal(false);
                    fetchCustomers(1);
                }}
            />
        </>
    );
};

export default CustomersPage;
